/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import DatePicker, {registerLocale, setDefaultLocale} from "react-datepicker";
import clsx from "clsx";
import Dates from "utils/Dates";
import {useTranslation} from "react-i18next";
import {ExclamationCircleIcon} from "@heroicons/react/24/outline";
import T from "components/T";
import {UseFormSetValue} from "react-hook-form/dist/types";
import {declareLocaleByConfig} from "utils/localeUtils";
import {LOCALE} from "helpers/config";
import {Portal} from "react-overlays";

interface BookingInputTimePickerProps {
    setValue: UseFormSetValue<any>;
    register: any;
    label?: string;
    value?: string | number;
    required?: boolean;
    errors: any;
    disabled?: boolean;
    name: string;
    className?: string;
    onlyTime?: boolean;
    onlyUpcomingDates?: boolean;
}

const BookingInputTimePicker = (props: BookingInputTimePickerProps) => {
    const {t} = useTranslation();
    const datePickerLocale = declareLocaleByConfig(LOCALE);
    registerLocale("locale", datePickerLocale);
    setDefaultLocale(datePickerLocale);
    const displayFormat = props.onlyTime ? "HH:mm" : "dd.MM.yyyy";
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);

    const dataHandler = (date): void => {
        setSelectedDate(props?.onlyTime ? date : new Date(date));
        props.setValue(props.name, props?.onlyTime ? date : Dates.ymd(date));
    };

    const CalendarContainer = ({children}) => {
        const el = document.getElementById("calendar-portal");
        return <Portal container={el}>{children}</Portal>;
    };

    useEffect(() => {
        if (props?.value) {
            setSelectedDate(new Date(props.value));
        }
    }, [props.value]);

    return (
        <div className={clsx("BookingInputTimePicker flex flex-col w-auto", props.className)}>
            {props.label && (
                <label
                    className={clsx(
                        "font-medium mb-1 flex w-full",
                        props.errors?.[props.name] && "text-red-500",
                    )}
                    htmlFor={"label-" + props.name}
                >
                    {t(props.label)}
                    {props.required && <em className={"text-red-500"}>*</em>}
                </label>
            )}
            <div
                className={"flex flex-row items-center shadow-sm shadow-black/10 rounded relative group [&>.react-datepicker-wrapper]:w-full"}>
                <DatePicker
                    className={clsx(
                        "w-full px-3 py-1.5 rounded-md border-gray-200 border z-1",
                        selectedDate ? "text-blue-400" : "text-dark-800 bg-gray-100",
                        props.errors?.[props.name] && "border-red-500",
                    )}
                    minDate={props?.onlyUpcomingDates ? new Date() : null}
                    onChange={dataHandler}
                    popperClassName={"!z-[50] [&>div>div>div]:!left-0 [&>div>div>div]:!right-0 [&>div>div>div]:!mx-auto"}
                    popperContainer={CalendarContainer}
                    selected={selectedDate}
                    dateFormat={displayFormat}
                    {...(props?.onlyTime && {
                        showTimeSelect: true,
                        showTimeSelectOnly: true,
                        timeIntervals: 10,
                        timeCaption: t("Time"),
                    })}
                />
            </div>
            <input {...props.register(props.name)} name={props.name} type="hidden" />
            {props.errors?.[props.name]?.message && (
                <div className={clsx("text-red-500 text-sm mt-1 flex flex-row items-center shrink-0")}>
                    <div className={clsx("shrink-0 flex flex-row items-center w-full")}>
                        <ExclamationCircleIcon className={"min-w-[18px] min-h-[18px] w-[18px] h-[18px] mr-0.5"} />
                        <T>{props.errors?.[props.name]?.message}</T>
                    </div>
                </div>
            )}
        </div>
    );
};

export default BookingInputTimePicker;